import React from 'react';
import { Box, Tooltip } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { authQueries } from '../lib/queries';

const DAY = 86_400_000;

/**
 * Header chip for a tenant on a trial (Tropos): days left and the end date. Read from /auth/me rather than the
 * plan saved at sign-in, so a trial the platform team extends or ends shows up without signing out.
 */
export default function TrialBadge({ isDark, enabled }: { isDark: boolean; enabled: boolean }) {
  const navigate = useNavigate();
  const { data } = useQuery({ ...authQueries.me(), enabled });
  const plan = data?.tenant?.plan;
  if (!plan || !plan.trial_end_date || !['trial', 'expired'].includes(plan.plan_status)) return null;

  const end = new Date(plan.trial_end_date);
  if (Number.isNaN(end.getTime())) return null;
  const msLeft = end.getTime() - Date.now();
  const ended = plan.plan_status === 'expired' || msLeft <= 0;
  const daysLeft = Math.ceil(msLeft / DAY);
  const endText = end.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });

  const label = ended ? 'Trial ended'
    : daysLeft <= 1 ? 'Trial ends today'
    : `Trial · ${daysLeft} days left`;
  // Colour tracks urgency: green with plenty of time, amber inside two weeks, orange inside a week, red at 3 days or ended.
  const tone = ended || daysLeft <= 3
    ? { fg: isDark ? '#F87171' : '#B91C1C', bg: isDark ? '#2a0f0f' : '#FEF2F2', line: isDark ? '#5b1d1d' : '#FECACA' }
    : daysLeft <= 7
      ? { fg: isDark ? '#FB923C' : '#C2410C', bg: isDark ? '#2a1608' : '#FFF7ED', line: isDark ? '#5a2e0e' : '#FED7AA' }
      : daysLeft <= 14
        ? { fg: isDark ? '#FACC15' : '#B45309', bg: isDark ? '#241f08' : '#FFFBEB', line: isDark ? '#4d3f0c' : '#FDE68A' }
        : { fg: isDark ? '#4ADE80' : '#15803D', bg: isDark ? '#0b2214' : '#F0FDF4', line: isDark ? '#14532d' : '#BBF7D0' };

  return (
    <Tooltip title={ended ? `Your trial ended on ${endText}. See plan options.` : `Your trial ends on ${endText}. See plan options.`}>
      <Box
        component="button"
        onClick={() => navigate('/settings?section=plan')}
        aria-label={`${label}, ends ${endText}`}
        sx={{
          display: { xs: 'none', sm: 'inline-flex' }, alignItems: 'center', gap: 1, px: 1.75, height: 38,
          borderRadius: '999px', cursor: 'pointer', fontFamily: 'inherit', fontSize: 14, fontWeight: 600,
          whiteSpace: 'nowrap', color: tone.fg, bgcolor: tone.bg, border: `1px solid ${tone.line}`,
          '&:hover': { borderColor: tone.fg },
          '&:focus-visible': { outline: `2px solid ${tone.fg}`, outlineOffset: 2 },
        }}
      >
        <Box component="span" sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: tone.fg }} />
        {label}
        <Box component="span" sx={{ fontWeight: 500, opacity: 0.8, display: { xs: 'none', md: 'inline' } }}>
          {ended ? '' : `· ends ${endText}`}
        </Box>
      </Box>
    </Tooltip>
  );
}
